import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import { useUIContext } from "../../../../context/UIContext";

export default function TextLengthManagement() {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];

  const { handleToastCenterTop, setIsLoadingModal } = useUIContext();

  // 기본 데이터 설정
  const defaultTextLengthData = [
    {
      field: "subjectComment",
      fieldName: "과목세부특기사항",
      min: "250",
      max: "300",
      isActive: false,
    },
    {
      field: "schoolLifeHrComment",
      fieldName: "행동특성 및 의견",
      min: "500",
      max: "600",
      isActive: false,
    },
  ];

  const [dataToSave, setDataToSave] = useState(defaultTextLengthData);
  const [flexibleDataId, setFlexibleDataId] = useState(null);

  // flexibleData 조회
  const {
    flexibleDataQuery: { data: flexibleData },
    createOrUpdateFlexibleData,
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "extraSettings",
    sub_category: "textLength",
  });

  // 데이터 로드
  useEffect(() => {
    if (flexibleData && flexibleData.length > 0) {
      // 데이터가 있으면 해당 데이터 사용
      const existingData = flexibleData[0];
      if (existingData.data_value && Array.isArray(existingData.data_value)) {
        setDataToSave(existingData.data_value);
        setFlexibleDataId(existingData.id); // id 저장
      } else {
        setDataToSave(defaultTextLengthData);
        setFlexibleDataId(existingData.id); // id는 있을 수 있음
      }
    } else {
      // 데이터가 없으면 기본 데이터 사용
      setDataToSave(defaultTextLengthData);
      setFlexibleDataId(null); // id 없음 (최초 생성)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexibleData]);

  // 입력값 변경 핸들러
  const handleChange = (rowIndex, field, value) => {
    const updatedRows = dataToSave.map((row, index) => {
      if (index === rowIndex) {
        return { ...row, [field]: value, isModified: true };
      }
      return row;
    });
    setDataToSave(updatedRows);
  };

  // 유효성 검사
  const validateData = (rowData) => {
    const min = parseInt(rowData.min);
    const max = parseInt(rowData.max);

    if (!rowData.fieldName || rowData.fieldName.trim() === "") {
      handleToastCenterTop("필드 이름을 입력해주세요.");
      return false;
    }

    if (isNaN(min) || min < 0) {
      handleToastCenterTop("최소 글자수는 0 이상의 숫자여야 합니다.");
      return false;
    }

    if (isNaN(max) || max < 1) {
      handleToastCenterTop("최대 글자수는 1 이상의 숫자여야 합니다.");
      return false;
    }

    if (min >= max) {
      handleToastCenterTop("최소 글자수는 최대 글자수보다 작아야 합니다.");
      return false;
    }

    if (max > 10000) {
      handleToastCenterTop("최대 글자수는 10000을 초과할 수 없습니다.");
      return false;
    }

    return true;
  };

  // 전체 저장 핸들러
  const handleSaveAll = () => {
    // 모든 항목 유효성 검사
    for (let i = 0; i < dataToSave.length; i++) {
      if (!validateData(dataToSave[i])) {
        return;
      }
    }

    // 저장할 데이터 준비 (isModified 제거)
    const dataToSubmit = dataToSave.map((row) => {
      const { isModified, ...rest } = row;
      return rest;
    });

    const submitData = {
      schoolid: schoolId,
      main_category: "extraSettings",
      sub_category: "textLength",
      data_value: dataToSubmit,
    };

    // id가 있으면 포함 (update), 없으면 제외 (create)
    if (flexibleDataId) {
      submitData.id = flexibleDataId;
    }

    setIsLoadingModal(true);
    createOrUpdateFlexibleData.mutate(submitData, {
      onSuccess: (response) => {
        setIsLoadingModal(false);
        handleToastCenterTop("전체 저장이 완료되었습니다.");

        // 최초 생성 후 id 저장 (서버에서 id를 return하는 경우)
        if (!flexibleDataId && response?.id) {
          setFlexibleDataId(response.id);
        }

        // 모든 isModified 플래그 제거
        setDataToSave((currentData) =>
          currentData.map((row) => {
            const { isModified, ...rest } = row;
            return rest;
          })
        );
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error);
        handleToastCenterTop(`저장 실패: ${error.message}`);
      },
    });
  };

  return (
    <>
      <div className="row mt-3">
        <div className="col-auto">
          <h3 className="fw-bold">
            {t("admin.extraSettings.textLengthManagement") ||
              "글자수 제한 설정"}
          </h3>
        </div>
        <div className="col-auto ms-auto">
          <button
            type="button"
            onClick={handleSaveAll}
            className="btn btn-success"
          >
            {t("common.labels.doSave") || "저장하기"}
          </button>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col" style={{ minWidth: "700px", overflowX: "auto" }}>
          <table className="table table-bordered text-center">
            <thead>
              <tr className="table-secondary">
                <th style={{ width: "8%" }}>No</th>
                <th style={{ width: "35%" }}>
                  {t("admin.extraSettings.fieldName") || "적용 영역"}
                </th>
                <th style={{ width: "15%" }}>
                  {t("admin.extraSettings.minLength") || "최소 글자수"}
                </th>
                <th style={{ width: "15%" }}>
                  {t("admin.extraSettings.maxLength") || "최대 글자수"}
                </th>
                <th style={{ width: "27%" }}>
                  {t("admin.extraSettings.isActive") || "활성화 여부"}
                </th>
              </tr>
            </thead>
            <tbody>
              {dataToSave &&
                dataToSave.map((row, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <span>{row.fieldName || ""}</span>
                    </td>
                    <td>
                      <input
                        className="input-like-span"
                        type="number"
                        min="0"
                        value={row.min || ""}
                        onChange={(e) =>
                          handleChange(index, "min", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="input-like-span"
                        type="number"
                        min="1"
                        value={row.max || ""}
                        onChange={(e) =>
                          handleChange(index, "max", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={row.isActive ? "active" : "inactive"}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "isActive",
                            e.target.value === "active"
                          )
                        }
                      >
                        <option value="inactive">
                          {t("common.status.inactive") || "비활성"}
                        </option>
                        <option value="active">
                          {t("common.status.active") || "활성"}
                        </option>
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col">
          <div className="alert alert-info">
            <h6 className="fw-bold">
              {t("admin.extraSettings.usage") || "사용 안내"}
            </h6>
            <ul className="mb-0">
              <li>최소/최대 글자수: 입력 가능한 글자수 범위를 지정합니다.</li>
              <li>활성화 여부: "활성"으로 설정하면 해당 제한이 적용됩니다.</li>
              <li>설정 완료 후 "저장" 버튼을 눌러 저장하세요.</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
